import React, { useEffect, useState } from 'react';
import { supabase, TABLES } from '../api/db';
import { Task, Priority, TaskStatus, Urgency, RiskLevel, Environment } from '../types';
import { Loader, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { addTaskChangeEntry } from '../api/taskTimeline';

interface TaskDetailEditorProps {
  task: Task;
  collectionName: string;
  onUpdated: (next: Task) => void;
  /** e.g. max height + scroll inside dashboard */
  className?: string;
}

function tsToDateInput(ts: string | null | undefined): string {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dateInputToIso(value: string): string | null {
  const v = value.trim();
  if (!v) return null;
  const d = new Date(v + 'T12:00:00');
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

const priorities: Priority[] = ['low', 'medium', 'high', 'critical'];
const statuses: TaskStatus[] = [
  'intake',
  'analyzing',
  'ai-processing',
  'neural-requested',
  'assigned',
  'in-progress',
  'completed',
  'cancelled',
];
const urgencies: Urgency[] = ['immediate', '24h', 'this-week', 'flexible'];
const risks: RiskLevel[] = ['low', 'moderate', 'high'];
const environments: Environment[] = ['development', 'staging', 'production', 'sandbox'];

const TaskDetailEditor: React.FC<TaskDetailEditorProps> = ({ task, collectionName, onUpdated, className }) => {
  const { currentUser } = useAuth();
  const [title, setTitle] = useState(task.title);
  const [summary, setSummary] = useState(task.summary);
  const [requirements, setRequirements] = useState(task.requirements);
  const [acceptanceCriteria, setAcceptanceCriteria] = useState(task.acceptanceCriteria);
  const [priority, setPriority] = useState<Priority>(task.priority);
  const [status, setStatus] = useState<TaskStatus>(task.status);
  const [urgency, setUrgency] = useState<Urgency>(task.urgency);
  const [riskLevel, setRiskLevel] = useState<RiskLevel>(task.riskLevel);
  const [environment, setEnvironment] = useState<Environment>(task.environment);
  const [category, setCategory] = useState(task.category);
  const [impact, setImpact] = useState(task.impact);
  const [repoUrl, setRepoUrl] = useState(task.repoUrl || '');
  const [communicationChannel, setCommunicationChannel] = useState(task.communicationChannel || '');
  const [dependencies, setDependencies] = useState(task.dependencies || '');
  const [estimatedHours, setEstimatedHours] = useState(
    task.estimatedHours != null ? String(task.estimatedHours) : ''
  );
  const [tags, setTags] = useState(Array.isArray(task.tags) ? task.tags.join(', ') : '');
  const [dueDate, setDueDate] = useState(tsToDateInput(task.dueDate));
  const [startDate, setStartDate] = useState(tsToDateInput(task.startDate ?? null));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setTitle(task.title);
    setSummary(task.summary);
    setRequirements(task.requirements);
    setAcceptanceCriteria(task.acceptanceCriteria);
    setPriority(task.priority);
    setStatus(task.status);
    setUrgency(task.urgency);
    setRiskLevel(task.riskLevel);
    setEnvironment(task.environment);
    setCategory(task.category);
    setImpact(task.impact);
    setRepoUrl(task.repoUrl || '');
    setCommunicationChannel(task.communicationChannel || '');
    setDependencies(task.dependencies || '');
    setEstimatedHours(task.estimatedHours != null ? String(task.estimatedHours) : '');
    setTags(Array.isArray(task.tags) ? task.tags.join(', ') : '');
    setDueDate(tsToDateInput(task.dueDate));
    setStartDate(tsToDateInput(task.startDate ?? null));
  }, [task]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const tagsArr = tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      const est =
        estimatedHours.trim() === '' ? null : Math.max(0, parseFloat(estimatedHours) || 0);
      const due = dateInputToIso(dueDate);
      const start = dateInputToIso(startDate);
      const now = new Date().toISOString();

      let completedAt = task.completedAt;
      if (status === 'completed' && task.status !== 'completed') {
        completedAt = now;
      } else if (status !== 'completed') {
        completedAt = null;
      }

      const payload = {
        title: title.trim(),
        summary: summary.trim(),
        requirements: requirements.trim(),
        acceptanceCriteria: acceptanceCriteria.trim(),
        priority,
        status,
        urgency,
        riskLevel,
        environment,
        category: category.trim(),
        impact: impact.trim(),
        repoUrl: repoUrl.trim(),
        communicationChannel: communicationChannel.trim(),
        dependencies: dependencies.trim(),
        estimatedHours: est,
        tags: tagsArr,
        dueDate: due,
        startDate: start,
        updatedAt: now,
        completedAt,
      };

      const targetTable = collectionName === TABLES.TASK_HATCH_SUBMISSIONS
        ? TABLES.TASK_HATCH_SUBMISSIONS
        : TABLES.TASKS;

      const { error } = await supabase
        .from(targetTable)
        .update({
          title: payload.title,
          summary: payload.summary,
          requirements: payload.requirements,
          acceptance_criteria: payload.acceptanceCriteria,
          priority: payload.priority,
          status: payload.status,
          urgency: payload.urgency,
          risk_level: payload.riskLevel,
          environment: payload.environment,
          category: payload.category,
          impact: payload.impact,
          repo_url: payload.repoUrl,
          communication_channel: payload.communicationChannel,
          dependencies: payload.dependencies,
          estimated_hours: payload.estimatedHours,
          tags: payload.tags,
          due_date: payload.dueDate,
          start_date: payload.startDate,
          updated_at: payload.updatedAt,
          completed_at: payload.completedAt,
        })
        .eq('id', task.id);
      if (error) throw error;

      // Auto-log changes to the task timeline
      if (currentUser) {
        const oldValues: Record<string, unknown> = {
          title: task.title,
          summary: task.summary,
          requirements: task.requirements,
          acceptanceCriteria: task.acceptanceCriteria,
          priority: task.priority,
          status: task.status,
          urgency: task.urgency,
          riskLevel: task.riskLevel,
          environment: task.environment,
          category: task.category,
          impact: task.impact,
          repoUrl: task.repoUrl || '',
          communicationChannel: task.communicationChannel || '',
          dependencies: task.dependencies || '',
          estimatedHours: task.estimatedHours,
          tags: task.tags,
          dueDate: task.dueDate,
          startDate: task.startDate ?? null,
          completedAt: task.completedAt,
        };
        try {
          await addTaskChangeEntry(task.id, currentUser.uid, oldValues, payload);
        } catch (changeErr) {
          console.error('Could not log task changes:', changeErr);
        }
      }

      const next: Task = {
        ...task,
        ...payload,
      };
      onUpdated(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={handleSave}
      className={`glass-panel rounded-3xl p-6 md:p-8 border-tech-cyan/20 space-y-6 min-h-0 ${className ?? ''}`}
    >
      <div>
        <h2 className="text-xl font-bold text-tech-text mb-1">Task workspace</h2>
        <p className="text-sm text-tech-text-muted">
          Edit the full brief, dates, and status. Changes save to this task for everyone with access.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-tech-text mb-1">Title</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          className="w-full rounded-xl bg-tech-dark border border-tech-border px-4 py-2.5 text-tech-text focus:outline-none focus:ring-2 focus:ring-tech-cyan/35"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-tech-text mb-1">Summary</label>
        <textarea
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          rows={3}
          className="w-full rounded-xl bg-tech-dark border border-tech-border px-4 py-3 text-tech-text focus:outline-none focus:ring-2 focus:ring-tech-cyan/35"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-tech-text mb-1">Start date (optional)</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full rounded-xl bg-tech-dark border border-tech-border px-4 py-2.5 text-tech-text"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-tech-text mb-1">Due date</label>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="w-full rounded-xl bg-tech-dark border border-tech-border px-4 py-2.5 text-tech-text"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-tech-text mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as TaskStatus)}
            className="w-full rounded-xl bg-tech-dark border border-tech-border px-4 py-2.5 text-tech-text"
          >
            {statuses.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-tech-text mb-1">Priority</label>
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value as Priority)}
            className="w-full rounded-xl bg-tech-dark border border-tech-border px-4 py-2.5 text-tech-text"
          >
            {priorities.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-tech-text mb-1">Urgency</label>
          <select
            value={urgency}
            onChange={(e) => setUrgency(e.target.value as Urgency)}
            className="w-full rounded-xl bg-tech-dark border border-tech-border px-4 py-2.5 text-tech-text"
          >
            {urgencies.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-tech-text mb-1">Risk</label>
          <select
            value={riskLevel}
            onChange={(e) => setRiskLevel(e.target.value as RiskLevel)}
            className="w-full rounded-xl bg-tech-dark border border-tech-border px-4 py-2.5 text-tech-text"
          >
            {risks.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-tech-text mb-1">Environment</label>
          <select
            value={environment}
            onChange={(e) => setEnvironment(e.target.value as Environment)}
            className="w-full rounded-xl bg-tech-dark border border-tech-border px-4 py-2.5 text-tech-text"
          >
            {environments.map((env) => (
              <option key={env} value={env}>
                {env}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-tech-text mb-1">Est. hours</label>
          <input
            type="number"
            min={0}
            step={0.5}
            value={estimatedHours}
            onChange={(e) => setEstimatedHours(e.target.value)}
            className="w-full rounded-xl bg-tech-dark border border-tech-border px-4 py-2.5 text-tech-text"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-tech-text mb-1">Category</label>
          <input
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full rounded-xl bg-tech-dark border border-tech-border px-4 py-2.5 text-tech-text"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-tech-text mb-1">Impact</label>
          <input
            value={impact}
            onChange={(e) => setImpact(e.target.value)}
            className="w-full rounded-xl bg-tech-dark border border-tech-border px-4 py-2.5 text-tech-text"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-tech-text mb-1">Tags (comma-separated)</label>
        <input
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          className="w-full rounded-xl bg-tech-dark border border-tech-border px-4 py-2.5 text-tech-text"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-tech-text mb-1">Requirements</label>
        <textarea
          value={requirements}
          onChange={(e) => setRequirements(e.target.value)}
          rows={6}
          className="w-full rounded-xl bg-tech-dark border border-tech-border px-4 py-3 text-tech-text whitespace-pre-wrap"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-tech-text mb-1">Acceptance criteria</label>
        <textarea
          value={acceptanceCriteria}
          onChange={(e) => setAcceptanceCriteria(e.target.value)}
          rows={4}
          className="w-full rounded-xl bg-tech-dark border border-tech-border px-4 py-3 text-tech-text"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-tech-text mb-1">Repo / link</label>
          <input
            value={repoUrl}
            onChange={(e) => setRepoUrl(e.target.value)}
            className="w-full rounded-xl bg-tech-dark border border-tech-border px-4 py-2.5 text-tech-text"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-tech-text mb-1">Communication</label>
          <input
            value={communicationChannel}
            onChange={(e) => setCommunicationChannel(e.target.value)}
            className="w-full rounded-xl bg-tech-dark border border-tech-border px-4 py-2.5 text-tech-text"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-tech-text mb-1">Dependencies</label>
        <textarea
          value={dependencies}
          onChange={(e) => setDependencies(e.target.value)}
          rows={2}
          className="w-full rounded-xl bg-tech-dark border border-tech-border px-4 py-3 text-tech-text"
        />
      </div>

      <div className="flex justify-end pt-2">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-tech-dark disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #00d9ff 0%, #0066ff 100%)' }}
        >
          {saving ? <Loader className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          Save task
        </button>
      </div>
    </form>
  );
};

export default TaskDetailEditor;
